import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ConversationListItemDto, ConversationTranscriptDto } from '@repo/shared-types';
import { apiFetch } from '../lib/api';

interface ConversationsPage {
  items: ConversationListItemDto[];
  nextCursor: string | null;
}

/**
 * `GET /api/bots/:botId/conversations` — keyset-paginated via `useInfiniteQuery` (spec
 * D3/M5 task-2). The list never auto-refreshes (D3: "polls nothing") and pagination is a
 * manual "Load more" button (`fetchNextPage`) — never infinite scroll — so React Query's
 * own page cache does the accumulation instead of hand-rolled `useEffect` + local state.
 */
export function useConversations(botId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ['bots', botId, 'conversations'],
    queryFn: ({ pageParam }) =>
      apiFetch<ConversationsPage>(
        `/api/bots/${botId}/conversations${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!botId,
  });
}

/** `GET /api/conversations/:id` — the full transcript for the currently selected row. */
export function useConversationTranscript(conversationId: string | null) {
  return useQuery({
    queryKey: ['conversations', conversationId],
    queryFn: () => apiFetch<ConversationTranscriptDto>(`/api/conversations/${conversationId}`),
    enabled: !!conversationId,
  });
}
